import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Chart as ChartJS,
  ChartEvent,
  ChartDataset,
  ActiveElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import type { ActiveUsersRow } from './analyticsTypes';

import { opsColors } from '../../../theme/opsTokens';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend, Filler);

interface ActiveUsersChartProps {
  rows: ActiveUsersRow[];
  /** Optional comparison-period rows. Rendered as a dashed series. */
  previousRows?: ActiveUsersRow[];
  /** Click handler — receives the day string for the clicked point. */
  onPointClick?: (day: string) => void;
  height?: number;
}

const ActiveUsersChart: React.FC<ActiveUsersChartProps> = ({
  rows,
  previousRows,
  onPointClick,
  height = 280,
}) => {
  const { t } = useTranslation('admin');
  const lineColor = opsColors.semantic.info.color;
  const previousColor = opsColors.neutral.inkFaint;

  const chartData = useMemo(() => {
    const datasets: ChartDataset<'line', number[]>[] = [
      {
        label: t('analytics.charts.distinctUsers'),
        data: rows.map((r) => r.distinct_users),
        fill: true,
        borderColor: lineColor,
        backgroundColor: alpha(lineColor, 0.15),
        tension: 0.3,
        pointRadius: 2,
      },
    ];
    if (previousRows && previousRows.length > 0) {
      const aligned = previousRows.slice(-rows.length).map((r) => r.distinct_users);
      while (aligned.length < rows.length) aligned.unshift(0);
      datasets.push({
        label: t('analytics.charts.previousPeriod'),
        data: aligned,
        fill: false,
        borderColor: previousColor,
        borderDash: [4, 4],
        backgroundColor: 'transparent',
        tension: 0.3,
        pointRadius: 0,
      });
    }
    return {
      labels: rows.map((r) => r.day),
      datasets,
    };
  }, [rows, previousRows, lineColor, previousColor, t]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: !!previousRows && previousRows.length > 0, position: 'top' as const, align: 'end' as const },
      title: { display: true, text: t('analytics.charts.activeUsersTitle'), font: { size: 14 } },
      tooltip: { mode: 'index' as const, intersect: false },
    },
    scales: {
      x: { ticks: { maxRotation: 45, minRotation: 45 } },
      y: { beginAtZero: true, title: { display: true, text: t('analytics.charts.usersAxis') } },
    },
    onClick: onPointClick
      ? (_e: ChartEvent, elements: ActiveElement[]) => {
          if (elements.length === 0) return;
          const idx = elements[0].index;
          const row = rows[idx];
          if (row) onPointClick(row.day);
        }
      : undefined,
    onHover: onPointClick
      ? (event: ChartEvent, elements: ActiveElement[]) => {
          const canvas = (event.native as MouseEvent | null)?.target as HTMLElement | null;
          if (canvas) canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
      : undefined,
  }), [previousRows, onPointClick, rows, t]);

  const rangeText = rows.length > 0
    ? `${rows[0].day} to ${rows[rows.length - 1].day}`
    : t('analytics.charts.activeUsersAriaNoData');

  return (
    <Box
      sx={{ height }}
      role="img"
      aria-label={t('analytics.charts.activeUsersAriaRange', { range: rangeText })}
    >
      <Line data={chartData} options={options} />
    </Box>
  );
};

export default React.memo(ActiveUsersChart);
